<template>
  <div 
    class="model-viewer-container"
    :style="{ height: heightValue + 'px' }"
    v-loading="isLoading"
    element-loading-text="加载中..."
    element-loading-spinner="el-icon-loading"
    element-loading-background="rgba(0, 0, 0, 0.2)"
  >
    <!-- 3D 画布容器 -->
    <div ref="container" class="viewer-canvas"></div>
    
    <!-- 控制工具栏 -->
    <div class="toolbar">
      <el-button-group>
        <el-tooltip content="重置视图" placement="top">
          <el-button @click="resetView" size="mini" icon="el-icon-refresh"></el-button>
        </el-tooltip>
        <el-tooltip content="放大" placement="top">
          <el-button @click="zoomIn" size="mini" icon="el-icon-zoom-in"></el-button>
        </el-tooltip>
        <el-tooltip content="缩小" placement="top">
          <el-button @click="zoomOut" size="mini" icon="el-icon-zoom-out"></el-button>
        </el-tooltip>
        <el-tooltip content="自动适配" placement="top">
          <el-button @click="autoFitModel" size="mini" icon="el-icon-full-screen"></el-button>
        </el-tooltip>
      </el-button-group>
      <!-- <div class="scale-control">
        <span>缩放: {{ Math.round(scaleFactor * 100) }}%</span>
        <el-slider 
          v-model="scaleFactor" 
          :min="0.1" 
          :max="1" 
          :step="0.1"
          @change="applyScale"
        />
      </div> -->
    </div>
    
    <!-- 错误提示 -->
    <el-alert 
      v-if="loadError" 
      type="error" 
      :closable="true"
      @close="loadError = null"
      class="error-alert"
    >
      <div class="error-content">
        <i class="el-icon-warning"></i>
        <span>模型加载失败: {{ loadError.message }}</span>
        <el-button type="text" @click.stop="reloadModel">重试</el-button>
      </div>
    </el-alert>
  </div>
</template>

<script>
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

export default {
  name: 'ModelViewer',
  props: {
    modelPath: {
      type: String,
      required: true,
      default: ''
    },
    autoRotate: {
      type: Boolean,
      default: true
    },
    height: {
      type: [Number, String],
      default: 400
    },
    // 新增：初始缩放比例
    initialScale: {
      type: Number,
      default: 0.1
    },
    // 新增：是否自动缩放
    autoScale: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      isLoading: false,
      progress: 0,
      loadError: null,
      renderer: null,
      scene: null,
      camera: null,
      controls: null,
      model: null,
      animationId: null,
      resizeObserver: null,
      // 新增：缩放控制
      scaleFactor: this.initialScale,
      originalScale: 0.1
    };
  },
  computed: {
    heightValue() {
      if (typeof this.height === 'number') return this.height;
      if (typeof this.height === 'string') {
        const num = parseInt(this.height);
        return isNaN(num) ? 400 : num;
      }
      return 400;
    }
  },
  mounted() {
    this.initThree();
  },
  beforeDestroy() {
    this.disposeAll();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  },
  watch: {
    modelPath: {
      immediate: true,
      handler(newPath, oldPath) {
        if (newPath && newPath !== oldPath && this.renderer) {
          this.loadModel();
        }
      }
    },
    autoRotate(newVal) {
      if (this.controls) {
        this.controls.autoRotate = newVal;
      }
    },
    scaleFactor(newVal) {
      if (this.model && newVal !== this.originalScale) {
        this.applyScale();
      }
    }
  },
  methods: {
    initThree() {
      try {
        const container = this.$refs.container;
        const width = container.clientWidth;
        const height = this.heightValue;

        // 创建场景
        this.scene = new THREE.Scene();
        // this.scene.background = new THREE.Color(0xf8f9fa);
        this.scene.background = null;
        
        // 创建相机
        this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 10000);
        this.camera.position.set(0, 0, 10);
        
        // 创建渲染器
        this.renderer = new THREE.WebGLRenderer({ 
          antialias: true,
          alpha: true
        });
        this.renderer.setSize(width, height);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        container.appendChild(this.renderer.domElement);
        
        // 添加轨道控制器
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.autoRotate = this.autoRotate;
        this.controls.autoRotateSpeed = 0.8;
        this.controls.screenSpacePanning = true;
        this.controls.minDistance = 1;
        this.controls.maxDistance = 1000;
        
        // 添加基础灯光
        this.addLights();
        
        // 启动渲染循环
        this.animate();
        
        // 初始化成功后加载模型
        this.$nextTick(() => {
          if (this.modelPath) {
            this.loadModel();
          }
        });
      } catch (error) {
        console.error('Three.js 初始化失败:', error);
        this.loadError = error;
        this.isLoading = false;
      }
    },
    
    addLights() {
      // 环境光
      const ambientLight = new THREE.AmbientLight(0xffffff, 1.2);
      this.scene.add(ambientLight);
      
      // 主光源
      const mainLight = new THREE.DirectionalLight(0xffffff, 1.5);
      mainLight.position.set(5, 5, 5);
      mainLight.castShadow = true;
      mainLight.shadow.mapSize.width = 1024;
      mainLight.shadow.mapSize.height = 1024;
      this.scene.add(mainLight);
      
      // 辅助光源
      const fillLight = new THREE.DirectionalLight(0xffffff, 0.5);
      fillLight.position.set(-5, 0, -5);
      this.scene.add(fillLight);
    },
    
    loadModel() {
      if (!this.modelPath) {
        this.loadError = new Error('模型路径为空');
        this.isLoading = false;
        return;
      }
      
      this.isLoading = true;
      this.progress = 0;
      this.loadError = null;
      
      try {
        const loader = new GLTFLoader();
        
        loader.load(
          this.modelPath,
          (gltf) => {
            // 清除旧模型
            this.disposeModel();
            
            this.model = gltf.scene;
            
            // 保存原始缩放
            this.originalScale = 1;
            
            // 应用初始缩放
            this.model.scale.set(this.scaleFactor, this.scaleFactor, this.scaleFactor);
            
            // 模型预处理
            this.model.traverse((object) => {
              if (object.isMesh) {
                object.castShadow = true;
                object.receiveShadow = true;
                // 优化大模型性能
                if (object.geometry) {
                  object.geometry.computeBoundingBox();
                  object.geometry.computeBoundingSphere();
                }
              }
            });
            
            // 智能缩放和居中
            this.autoFitModel();
            
            // 添加到场景
            this.scene.add(this.model);
            
            this.$emit('load', gltf);
          },
          (xhr) => {
            if (xhr.lengthComputable) {
              this.progress = Math.round((xhr.loaded / xhr.total) * 100);
            }
          },
          (error) => {
            console.error('模型加载失败:', error);
            this.loadError = error;
            this.$emit('error', error);
          }
        );
      } catch (error) {
        console.error('模型加载过程出错:', error);
        this.loadError = error;
        this.$emit('error', error);
      } finally {
        // 确保加载状态关闭
        setTimeout(() => {
          this.isLoading = false;
        }, 300);
      }
    },
    
    autoFitModel() {
      if (!this.model) return;
      
      // 1. 获取模型包围盒
      const box = new THREE.Box3().setFromObject(this.model);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      
      // 2. 计算对角线长度（更准确的尺寸测量）
      const diagonal = Math.sqrt(size.x * size.x + size.y * size.y + size.z * size.z);
      
      // 3. 根据对角线计算最佳相机距离
      const fov = this.camera.fov * (Math.PI / 180);
      const cameraZ = Math.abs(diagonal / 4 * Math.tan(fov / 2));
      
      // 4. 设置合理的距离范围
      const minDistance = 5;
      const maxDistance = 1000;
      let finalDistance = Math.max(minDistance, Math.min(cameraZ, maxDistance));
      
      // 5. 特殊处理超大模型
      if (diagonal > 1000) {
        // 对于超大型模型（如建筑），使用对数缩放
        finalDistance = Math.log10(diagonal) * 50;
        this.scaleFactor = Math.min(1, 100 / diagonal);
        this.applyScale();
      } 
      // 6. 特殊处理超小模型
      else if (diagonal < 0.1) {
        finalDistance = 2;
        this.scaleFactor = Math.min(5, 1 / diagonal);
        this.applyScale();
      }
      
      // 7. 设置相机位置
      this.camera.position.set(
        center.x, 
        center.y, 
        center.z + finalDistance
      );
      
      // 8. 设置控制器目标
      this.controls.target.copy(center);
      this.controls.update();
      
      // 9. 重置相机朝向
      this.camera.lookAt(center);
      
      // 10. 保存初始状态
      this.controls.saveState();
      
      console.log('模型尺寸:', {
        width: size.x.toFixed(2),
        height: size.y.toFixed(2),
        depth: size.z.toFixed(2),
        diagonal: diagonal.toFixed(2),
        cameraDistance: finalDistance.toFixed(2)
      });
      
      // 11. 添加网格辅助（调试用）
      this.addGridHelper(diagonal);
    },
    
    addGridHelper(diagonal) {
      // 移除旧的网格
      this.scene.children.forEach(child => {
        if (child.name === 'gridHelper') {
          this.scene.remove(child);
        }
      });
      
      // 创建新网格
      const gridSize = Math.ceil(diagonal / 10) * 10;
      const gridDivisions = Math.max(10, Math.floor(gridSize / 2));
      
      const gridHelper = new THREE.GridHelper(gridSize, gridDivisions, 0x444444, 0x888888);
      gridHelper.name = 'gridHelper';
      gridHelper.position.y = -0.01; // 略低于模型
      this.scene.add(gridHelper);
      
      // 添加坐标轴辅助
      const axesHelper = new THREE.AxesHelper(Math.min(5, gridSize / 5));
      axesHelper.name = 'axesHelper';
      this.scene.add(axesHelper);
    },
    
    applyScale() {
      if (!this.model) return;
      
      // 应用缩放
      this.model.scale.set(
        this.scaleFactor, 
        this.scaleFactor, 
        this.scaleFactor
      );
      
      // 重新计算位置
      this.autoFitModel();
    },
    
    resetView() {
      if (this.controls) {
        this.controls.reset();
        this.scaleFactor = this.initialScale;
        this.applyScale();
      }
    },
    
    zoomIn() {
      if (this.camera.position.z > 1) {
        this.camera.position.z *= 0.8;
        this.controls.update();
      }
    },
    
    zoomOut() {
      if (this.camera.position.z < 5000) {
        this.camera.position.z *= 1.25;
        this.controls.update();
      }
    },
    
    disposeModel() {
      if (this.model) {
        this.scene.remove(this.model);
        this.model.traverse((object) => {
          if (object.geometry) object.geometry.dispose();
          if (object.material) {
            if (Array.isArray(object.material)) {
              object.material.forEach(material => {
                if (material.dispose) material.dispose();
              });
            } else if (object.material.dispose) {
              object.material.dispose();
            }
          }
        });
        this.model = null;
      }
      
      // 移除辅助网格
      this.scene.children.forEach(child => {
        if (child.name === 'gridHelper' || child.name === 'axesHelper') {
          this.scene.remove(child);
        }
      });
    },
    
    animate() {
      this.animationId = requestAnimationFrame(this.animate);
      if (this.controls) this.controls.update();
      if (this.renderer) {
        this.renderer.render(this.scene, this.camera);
      }
    },
    
    setupResizeObserver() {
      if (typeof window.ResizeObserver !== 'function') {
        window.addEventListener('resize', this.handleResize);
        return;
      }
      
      this.resizeObserver = new ResizeObserver(() => {
        this.handleResize();
      });
      
      this.resizeObserver.observe(this.$refs.container);
    },
    
    handleResize() {
      if (!this.renderer || !this.camera) return;
      
      const container = this.$refs.container;
      const width = container.clientWidth;
      const height = this.heightValue;
      
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      
      this.renderer.setSize(width, height);
    },
    
    reloadModel() {
      this.loadError = null;
      this.loadModel();
    },
    
    disposeAll() {
      // 停止动画
      if (this.animationId) {
        cancelAnimationFrame(this.animationId);
        this.animationId = null;
      }
      
      // 移除 resize 事件
      if (typeof window.ResizeObserver !== 'function') {
        window.removeEventListener('resize', this.handleResize);
      }
      
      // 释放模型
      this.disposeModel();
      
      // 释放控制器
      if (this.controls) {
        this.controls.dispose();
        this.controls = null;
      }
      
      // 释放渲染器
      if (this.renderer) {
        this.renderer.dispose();
        const container = this.$refs.container;
        if (container && container.contains(this.renderer.domElement)) {
          container.removeChild(this.renderer.domElement);
        }
        this.renderer = null;
      }
      
      // 释放灯光
      if (this.scene) {
        this.scene.children.forEach((child) => {
          if (child.isLight) {
            if (child.dispose) child.dispose();
          }
        });
        this.scene = null;
      }
      
      this.camera = null;
    }
  }
};
</script>

<style lang="less" scoped>
.model-viewer-container {
  position: relative;
  width: 100%;
  // background: #f8f9fa;
  background: transparent;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  
  .viewer-canvas {
    width: 100%;
    height: 100%;
    background: transparent !important;
  }
  
  .toolbar {
    position: absolute;
    bottom: 15px;
    right: 15px;
    z-index: 20;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 10px;
    background: #363b46d9;
    padding: 8px 12px;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    
    .el-button-group {
      .el-button {
        padding: 5px;
      }
    }
    
    .scale-control {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 200px;
      
      span {
        font-size: 12px;
        color: #606266;
        min-width: 50px;
      }
      
      /deep/ .el-slider {
        flex: 1;
        
        .el-slider__button {
          width: 14px;
          height: 14px;
        }
      }
    }
  }
  
  .error-alert {
    position: absolute;
    top: 15px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 20;
    width: 90%;
    max-width: 500px;
    
    .error-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      
      i {
        font-size: 18px;
        margin-right: 8px;
        color: #f56c6c;
      }
      
      span {
        flex: 1;
        margin-right: 15px;
      }
      
      .el-button {
        padding: 0;
        height: auto;
        color: #409eff;
      }
    }
  }
}
/deep/.el-button{
  background-color: #636d83;
  color: #fff;
}
</style>